import * as React from 'react';
import { FunctionComponent } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Aerospace } from './AerospacePage';
import LinkButtons, { linkButtons } from './LinkButtons';
import ResponsiveSplitSection from '../common/ResponsiveSplitSection';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { InternalLinkSection } from '../common/internalLinkSection';
import { GetResponsiveWidth } from '../common/TabsComponent';
import type { InternalLinkSectionElement } from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';

interface AerospaceTemplateProps {
    aerospaces: Aerospace[];
    aerospaceLinkButtons: linkButtons[];
    defenseLinkButtons: linkButtons[];
    avionicsLinkButtons: linkButtons[];
    temperatureSensorLinks: InternalLinkSectionElement;
}

const AerospaceTemplate: FunctionComponent<AerospaceTemplateProps> = ({
    aerospaces,
    aerospaceLinkButtons,
    defenseLinkButtons,
    avionicsLinkButtons,
    temperatureSensorLinks,
}) => {
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const hasTemperatureSensorLinks = temperatureSensorLinks.links.length > 0;

    const renderAerospace = (aerospace: Aerospace, linkButtons: linkButtons[]) => {
        return !aerospace.withButton ? (
            <>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '20px',
                        fontWeight: 600,
                        lineHeight: '30px',
                        textAlign: 'left',
                        marginTop: isScreenMd || isScreenSm || isScreenXs ? '1rem' : '0',
                    }}
                >
                    {aerospace.title}
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        textAlign: 'left',
                        marginTop: '1rem',
                    }}
                    dangerouslySetInnerHTML={{ __html: aerospace.description }}
                ></Typography>
                {aerospace.linkTitle && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            width: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '24px',
                                textAlign: 'left',
                                marginTop: '1rem',
                            }}
                        >
                            {aerospace.linkTitle}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ marginTop: '1rem' }}>
                    <LinkButtons linkButtons={linkButtons} />
                </Box>
            </>
        ) : (
            <>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '20px',
                        fontWeight: 600,
                        lineHeight: '30px',
                        textAlign: 'left',
                        marginTop: isScreenMd || isScreenSm || isScreenXs ? '1rem' : '0',
                    }}
                >
                    {aerospace.title}
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        textAlign: 'left',
                        marginTop: isScreenMd || isScreenSm || isScreenXs ? '1rem' : '0',
                    }}
                    dangerouslySetInnerHTML={{ __html: aerospace.description }}
                ></Typography>
                <Box sx={{ marginTop: '3rem' }}>
                    <Button
                        onClick={() => (window.location.href = aerospace.buttonLink || '')}
                        sx={{
                            width: '150px',
                            height: '40px',
                            padding: '8px 10px',
                            gap: '10px',
                            borderRadius: '5px',
                            backgroundColor: '#E5673E',
                            color: 'white',
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            lineHeight: '24px',
                            transition: 'none',
                            textTransform: 'capitalize',
                            '&:hover': {
                                background: 'linear-gradient(0deg, #B75332 0%, #E5673E 100%)',
                            },
                        }}
                    >
                        {aerospace.buttonText || 'Products'}
                    </Button>
                </Box>
            </>
        );
    };

    const aerospaceSections = [
        {
            aerospace: aerospaces[0],
            backgroundColor: '#F2F2F2',
            linkButtons: aerospaceLinkButtons,
            bigScreenTextLeft: true,
        },
        {
            aerospace: aerospaces[1],
            backgroundColor: 'white',
            linkButtons: defenseLinkButtons,
            bigScreenTextLeft: false,
        },
        {
            aerospace: aerospaces[2],
            backgroundColor: '#F2F2F2',
            linkButtons: avionicsLinkButtons,
            bigScreenTextLeft: true,
        },
        {
            aerospace: aerospaces[3],
            backgroundColor: 'white',
            linkButtons: aerospaceLinkButtons,
            bigScreenTextLeft: false,
        },
    ];

    return (
        <>
            {aerospaceSections.map((section, index) =>
                section.aerospace ? (
                    <ResponsiveSplitSection
                        key={index}
                        textContent={renderAerospace(section.aerospace, section.linkButtons)}
                        backgroundColor={section.backgroundColor}
                        smallScreenTextFirst={false}
                        imageSrc={section.aerospace.image ?? ''}
                        bigScreenAlignItem="flex-start"
                        bigScreenTextLeft={section.bigScreenTextLeft}
                    />
                ) : null
            )}
            {hasTemperatureSensorLinks ? (
                <InternalLinkSection
                    section={temperatureSensorLinks}
                    contentPadding={{
                        width: GetResponsiveWidth,
                        margin: 'auto',
                        alignItems: 'flex-start',
                    }}
                />
            ) : null}
            <Box sx={{ backgroundColor: 'white', height: '10px' }}></Box>
        </>
    );
};

export default AerospaceTemplate;
